"use client";

import * as React from "react";
import { Volume2, VolumeX, Maximize2, Minimize2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { carrotSound } from "./carrot-sound";
import { ASSET, CARROT_ANSWERS, CRT_CSS } from "./carrot-stage";
import { FitText } from "./fit-text";
import type { PlayQuestionDTO, PlayResult } from "@/types/quiz";

type Screen = "intro" | "guide" | "select" | "question" | "correct" | "incorrect" | "complete";

function fmtTime(ms: number): string {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * "Hái Cà Rốt" — game trắc nghiệm nông trại (bộ ảnh riêng). Chọn một củ cà rốt →
 * trả lời câu hỏi: đúng thì thu hoạch (củ biến mất), sai thì củ héo xám. Hết lượt
 * hiện tổng số củ thu hoạch. Cùng `QuizGameProps` như các game khác nên tự chạy
 * ở chơi thử / giao bài / phụ huynh. Sân khấu 16:9 container-query + toàn màn hình.
 */
export function CarrotHarvest({
  questions,
  timeLimitSec = 0,
  onFinish,
  resultSlot,
  onReplay,
}: {
  questions: PlayQuestionDTO[];
  timeLimitSec?: number;
  onFinish: (r: PlayResult) => void;
  resultSlot?: React.ReactNode;
  onReplay?: () => void;
}) {
  const total = questions.length;
  const [screen, setScreen] = React.useState<Screen>("intro");
  const [current, setCurrent] = React.useState<number | null>(null);
  const [selected, setSelected] = React.useState<number | null>(null);
  const [results, setResults] = React.useState<Record<number, boolean>>({});
  const [muted, setMuted] = React.useState(false);
  const [isFull, setIsFull] = React.useState(false);
  const [elapsed, setElapsed] = React.useState(0);
  const [finishMs, setFinishMs] = React.useState(0);

  const stageRef = React.useRef<HTMLDivElement>(null);
  const answersRef = React.useRef<{ questionId: string; chosenIndex: number }[]>([]);
  const startRef = React.useRef<number>(Date.now());
  const finishedRef = React.useRef(false);

  const answeredCount = Object.keys(results).length;
  const harvested = Object.values(results).filter(Boolean).length;

  React.useEffect(() => {
    const onFs = () => setIsFull(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const finish = React.useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    const timeMs = Date.now() - startRef.current;
    setFinishMs(timeMs);
    const byId = new Map(questions.map((qq) => [qq.id, qq.correctIndex]));
    const correct = answersRef.current.filter((a) => a.chosenIndex === byId.get(a.questionId)).length;
    carrotSound.play("finish");
    onFinish({ answers: answersRef.current, timeMs, correctCount: correct });
  }, [onFinish, questions]);

  // Đồng hồ (nếu có giới hạn) — hết giờ tự kết thúc.
  React.useEffect(() => {
    if (screen === "intro" || screen === "guide" || screen === "complete" || timeLimitSec <= 0) return;
    const id = setInterval(() => {
      const e = Date.now() - startRef.current;
      setElapsed(e);
      if (e >= timeLimitSec * 1000) {
        setScreen("complete");
        finish();
      }
    }, 250);
    return () => clearInterval(id);
  }, [screen, timeLimitSec, finish]);

  /** Xóa toàn bộ tiến trình ván chơi (dùng cho về trang chủ / bắt đầu / chơi lại). */
  function resetState() {
    finishedRef.current = false;
    answersRef.current = [];
    startRef.current = Date.now();
    setResults({});
    setSelected(null);
    setCurrent(null);
  }

  function beginPlay() {
    carrotSound.unlock();
    carrotSound.play("click");
    resetState();
    setScreen("select");
  }

  /** Về trang chủ = RESET lại game. */
  function goHome() {
    carrotSound.play("click");
    resetState();
    setScreen("intro");
  }

  function toggleMute() {
    setMuted(carrotSound.toggle());
  }
  async function toggleFullscreen() {
    const el = stageRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await el.requestFullscreen();
    } catch {
      /* bỏ qua */
    }
  }

  function openQuestion(idx: number) {
    if (results[idx] !== undefined) return;
    carrotSound.play("click");
    setCurrent(idx);
    setSelected(null);
    setScreen("question");
  }

  function submit() {
    if (current === null || selected === null) return;
    const q = questions[current];
    if (!q) return;
    const isCorrect = selected === q.correctIndex;
    answersRef.current = [...answersRef.current, { questionId: q.id, chosenIndex: selected }];
    setResults((r) => ({ ...r, [current]: isCorrect }));
    carrotSound.play(isCorrect ? "correct" : "wrong");
    if (isCorrect) setTimeout(() => carrotSound.play("harvest"), 260);
    setScreen(isCorrect ? "correct" : "incorrect");
  }

  function afterFeedback() {
    carrotSound.play("click");
    // Đã trả lời hết → sang màn hoàn thành.
    if (answersRef.current.length >= total) {
      setScreen("complete");
      finish();
    } else {
      setScreen("select");
    }
  }

  function goComplete() {
    carrotSound.play("click");
    setScreen("complete");
    finish();
  }

  function replay() {
    if (onReplay) {
      onReplay();
      return;
    }
    resetState();
    setScreen("select");
  }

  const q = current !== null ? questions[current] : undefined;
  const timeLeft = timeLimitSec > 0 ? Math.max(0, timeLimitSec - Math.floor(elapsed / 1000)) : 0;
  const scoreImg = `${ASSET}/complete/so_${Math.min(harvested, 8)}.png`;

  return (
    <div
      ref={stageRef}
      className={cn("crt select-none", isFull ? "grid h-screen w-screen place-items-center bg-[#0767a8]" : "")}
    >
      <style>{CRT_CSS}</style>

      <div
        className={cn(
          "crt-frame relative overflow-hidden shadow-lg",
          isFull ? "aspect-[16/9] h-full max-h-full w-auto max-w-full" : "aspect-[16/9] w-full rounded-2xl"
        )}
      >
        {/* Điều khiển: âm thanh · toàn màn hình — xếp DỌC ở cạnh phải, dưới nút Hoàn thành. */}
        <div className="absolute right-[3.5%] top-[27%] z-40 flex flex-col items-center gap-[1.6cqh]">
          <button type="button" onClick={toggleMute} aria-label={muted ? "Bật âm thanh" : "Tắt âm thanh"} className="crt-ctrl">
            {muted ? <VolumeX className="size-full" aria-hidden /> : <Volume2 className="size-full" aria-hidden />}
          </button>
          <button type="button" onClick={toggleFullscreen} aria-label={isFull ? "Thoát toàn màn hình" : "Toàn màn hình"} className="crt-ctrl">
            {isFull ? <Minimize2 className="size-full" aria-hidden /> : <Maximize2 className="size-full" aria-hidden />}
          </button>
        </div>

        {/* ===== INTRO ===== */}
        {screen === "intro" && (
          <div className="crt-screen absolute inset-0">
            <img src={`${ASSET}/home/background_carrot_farm.png`} alt="" className="absolute inset-0 h-full w-full object-cover" aria-hidden />
            <img src={`${ASSET}/home/title_hai_ca_rot.png`} alt="Hái Cà Rốt" className="absolute left-1/2 top-[3.6%] w-[52%] -translate-x-1/2 drop-shadow" />
            <img src={`${ASSET}/home/tho_trang_cam_ca_rot.png`} alt="" className="crt-rabbit-float absolute bottom-[3%] left-[3.2%] w-[26%]" aria-hidden />
            <img src={`${ASSET}/home/tho_be_cam_ca_rot.png`} alt="" className="crt-rabbit-float absolute bottom-[3%] right-[4%] w-[23%]" aria-hidden style={{ animationDelay: "-0.8s" }} />
            <button type="button" onClick={beginPlay} className="crt-btn crt-btn-cx" style={{ left: "50%", top: "59%", width: "35%" }}>
              <img src={`${ASSET}/home/button_bat_dau_choi.png`} alt="Bắt đầu chơi" />
            </button>
            <button type="button" onClick={() => { carrotSound.play("click"); setScreen("guide"); }} className="crt-btn crt-btn-cx" style={{ left: "50%", top: "78.5%", width: "28%" }}>
              <img src={`${ASSET}/home/button_huong_dan_choi.png`} alt="Hướng dẫn chơi" />
            </button>
          </div>
        )}

        {/* ===== GUIDE ===== */}
        {screen === "guide" && (
          <div className="crt-screen absolute inset-0">
            <img src={`${ASSET}/guide/background_carrot_farm.png`} alt="" className="absolute inset-0 h-full w-full object-cover" aria-hidden />
            <img src={`${ASSET}/guide/bang_huong_dan_choi.png`} alt="Hướng dẫn chơi" className="absolute left-[53%] top-[1.5%] w-[74%] -translate-x-1/2 drop-shadow" />
            <img src={`${ASSET}/guide/tho_cam_but_chi.png`} alt="" className="crt-rabbit-float absolute bottom-[2%] left-[0.5%] w-[23%]" aria-hidden />
            <button type="button" onClick={goHome} className="crt-btn" style={{ left: "22%", bottom: "2%", width: "22%" }}>
              <img src={`${ASSET}/guide/button_quay_lai_trang_chu.png`} alt="Quay lại trang chủ" />
            </button>
            <button type="button" onClick={beginPlay} className="crt-btn" style={{ right: "15%", bottom: "1%", width: "32%" }}>
              <img src={`${ASSET}/guide/button_bat_dau_choi.png`} alt="Bắt đầu chơi" />
            </button>
          </div>
        )}

        {/* ===== SELECT ===== */}
        {screen === "select" && (
          <div className="crt-screen absolute inset-0">
            <img src={`${ASSET}/select/background_dat_phang.png`} alt="" className="absolute inset-0 h-full w-full object-cover" aria-hidden />
            <img src={`${ASSET}/select/bien_hai_ca_rot.png`} alt="Hái Cà Rốt" className="absolute left-1/2 top-[1%] w-[36%] max-h-[21.5%] -translate-x-1/2 object-contain" />
            <img src={`${ASSET}/select/bien_chon_ca_rot.png`} alt="Chọn một củ cà rốt để trả lời câu hỏi" className="absolute left-1/2 top-[22%] w-[38%] max-h-[23.5%] -translate-x-1/2 object-contain" />
            <img src={`${ASSET}/select/tho_trang_cam_ca_rot.png`} alt="" className="crt-rabbit-float absolute bottom-[1.5%] left-[1.5%] w-[18%] max-h-[47%] object-contain" aria-hidden />

            <button type="button" onClick={goHome} className="crt-btn" style={{ left: "2.2%", top: "2%", width: "9.5%", height: "22%" }}>
              <img src={`${ASSET}/select/button_ve_trang_chu.png`} alt="Về trang chủ" />
            </button>
            <button type="button" onClick={goComplete} disabled={answeredCount === 0} className="crt-btn" style={{ right: "2.2%", top: "2%", width: "9.5%", height: "22%" }}>
              <img src={`${ASSET}/select/button_hoan_thanh.png`} alt="Hoàn thành" />
            </button>

            {timeLimitSec > 0 && (
              <div className="absolute left-1/2 top-[45.5%] z-10 -translate-x-1/2 rounded-full bg-amber-900/85 px-[2.4cqw] py-[0.6cqh] font-bold text-white" style={{ fontSize: "clamp(11px,1.9cqw,22px)" }}>
                ⏱ {timeLeft}s · Đã hái {harvested}/{total}
              </div>
            )}

            {/* Luống cà rốt */}
            <div className="absolute flex flex-wrap content-center items-end justify-center gap-x-[3%] gap-y-[2%]" style={{ left: "20%", right: "6%", top: "47%", bottom: "5%" }}>
              {questions.map((_, i) => {
                const res = results[i];
                const answered = res !== undefined;
                const n = (i % 8) + 1;
                const src = res === false ? `${ASSET}/select/ca_rot_xam_${n}.png` : `${ASSET}/select/ca_rot_${n}.png`;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => openQuestion(i)}
                    disabled={answered}
                    aria-label={`Củ cà rốt ${i + 1}${res === false ? " (đã trả lời sai)" : ""}`}
                    className={cn("crt-carrot", res === false && "crt-carrot-wrong", res === true && "crt-carrot-done")}
                    style={{ width: total <= 4 ? "22%" : "20%", height: "46%" }}
                  >
                    <img src={src} alt="" aria-hidden />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ===== QUESTION ===== */}
        {screen === "question" && q && (
          <div className="crt-screen absolute inset-0">
            <img src={`${ASSET}/question/background_dat_phang.png`} alt="" className="absolute inset-0 h-full w-full object-cover" aria-hidden />
            <img src={`${ASSET}/question/bien_cau_hoi.png`} alt="Câu hỏi" className="absolute left-1/2 top-[1.8%] w-[39%] max-h-[18%] -translate-x-1/2 object-contain" />
            <img src={`${ASSET}/question/tho_trang_cam_ca_rot.png`} alt="" className="crt-rabbit-float absolute bottom-[1.5%] left-[1.5%] w-[18%] max-h-[47%] object-contain" aria-hidden />
            <button type="button" onClick={goHome} className="crt-btn" style={{ left: "2.2%", top: "2%", width: "9.5%", height: "22%" }}>
              <img src={`${ASSET}/question/button_ve_trang_chu.png`} alt="Về trang chủ" />
            </button>

            {/* Khung câu hỏi */}
            <div className="absolute grid place-items-center" style={{ left: "24%", top: "19%", width: "60%", height: "25%" }}>
              <img src={`${ASSET}/question/khung_cau_hoi.png`} alt="" className="absolute inset-0 h-full w-full object-fill" aria-hidden />
              <div className="crt-qtext absolute" style={{ inset: "16% 8% 14% 8%" }}>
                <FitText text={q.prompt} maxPx={56} minPx={13} />
              </div>
            </div>

            {/* Đáp án 2×2 */}
            <div className="absolute grid grid-cols-2 grid-rows-2 gap-x-[3%] gap-y-[4%]" style={{ left: "24%", right: "12%", top: "47%", bottom: "18%" }}>
              {CARROT_ANSWERS.map((a, i) => {
                const opt = q.options[i];
                if (opt === undefined) return <span key={i} aria-hidden />;
                const isSel = selected === i;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { carrotSound.play("click"); setSelected(i); }}
                    className={cn("crt-answer", isSel && "crt-answer-selected")}
                  >
                    <img src={a.img} alt="" aria-hidden />
                    <div className="crt-atext absolute" style={{ top: "16%", bottom: "16%", left: "22%", right: "6%" }}>
                      <FitText text={opt} maxPx={30} minPx={11} />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Nút tiếp theo (bật khi đã chọn đáp án) */}
            <button type="button" onClick={submit} disabled={selected === null} className="crt-btn" style={{ right: "3%", bottom: "2.5%", width: "23%" }}>
              <img src={`${ASSET}/question/button_tiep_theo.png`} alt="Tiếp theo" />
            </button>
          </div>
        )}

        {/* ===== FEEDBACK: ĐÚNG ===== */}
        {screen === "correct" && (
          <div className="crt-screen absolute inset-0">
            <img src={`${ASSET}/feedback/background_dat_phang.png`} alt="" className="absolute inset-0 h-full w-full object-cover" aria-hidden />
            <div className="absolute left-1/2 top-[9%] w-[61%] -translate-x-1/2">
              <img src={`${ASSET}/feedback/khung_dung_roi.png`} alt="Đúng rồi!" className="crt-panel-in w-full object-contain" />
            </div>
            <img src={`${ASSET}/feedback/tho_trang_cam_ca_rot.png`} alt="" className="crt-rabbit-happy absolute bottom-[1.5%] left-[1.5%] w-[20%] max-h-[49%] object-contain" aria-hidden />
            <button type="button" onClick={afterFeedback} className="crt-btn" style={{ right: "3%", bottom: "2.5%", width: "23%" }}>
              <img src={`${ASSET}/feedback/button_tiep_theo.png`} alt={answersRef.current.length >= total ? "Xem kết quả" : "Tiếp tục"} />
            </button>
          </div>
        )}

        {/* ===== FEEDBACK: SAI ===== */}
        {screen === "incorrect" && (
          <div className="crt-screen absolute inset-0">
            <img src={`${ASSET}/feedback/background_dat_phang.png`} alt="" className="absolute inset-0 h-full w-full object-cover" aria-hidden />
            <div className="absolute left-1/2 top-[9%] w-[56%] -translate-x-1/2">
              <img src={`${ASSET}/feedback/khung_tiec_qua.png`} alt="Tiếc quá! Chưa đúng rồi!" className="crt-panel-in w-full object-contain" />
            </div>
            <img src={`${ASSET}/feedback/tho_trang_cam_ca_rot.png`} alt="" className="crt-rabbit-float absolute bottom-[1.5%] left-[1.5%] w-[20%] max-h-[49%] object-contain" aria-hidden />
            <button type="button" onClick={afterFeedback} className="crt-btn" style={{ right: "3%", bottom: "2.5%", width: "23%" }}>
              <img src={`${ASSET}/feedback/button_tiep_theo.png`} alt={answersRef.current.length >= total ? "Xem kết quả" : "Tiếp tục"} />
            </button>
          </div>
        )}

        {/* ===== COMPLETE ===== */}
        {screen === "complete" && (
          <div className="crt-screen absolute inset-0 overflow-y-auto">
            <img src={`${ASSET}/complete/background_dat_phang.png`} alt="" className="absolute inset-0 h-full w-full object-cover" aria-hidden />
            <button type="button" onClick={goHome} className="crt-btn" style={{ left: "2.2%", top: "2%", width: "9.5%", height: "22%" }}>
              <img src={`${ASSET}/complete/button_ve_trang_chu.png`} alt="Về trang chủ" />
            </button>

            {finishMs > 0 && (
              <div
                className="absolute bottom-[3%] right-[3%] z-20 rounded-full bg-amber-900/85 px-[3cqw] py-[0.9cqh] font-extrabold text-white shadow"
                style={{ fontSize: "clamp(11px,2cqw,24px)" }}
              >
                ⏱ {fmtTime(finishMs)}
              </div>
            )}

            {resultSlot ? (
              // Có bảng xếp hạng (phụ huynh / chơi thử): bố cục dọc, cuộn được.
              <div className="relative z-10 flex min-h-full flex-col items-center justify-center gap-[2%] px-[5%] py-[4%]">
                <div className="relative w-[62%]">
                  <img src={`${ASSET}/complete/khung_hoan_thanh.png`} alt="Hoàn thành!" className="w-full object-contain" />
                  <img src={scoreImg} alt={`${harvested}`} className="crt-scorepop absolute left-1/2 top-[57%] w-[16%]" style={{ transform: "translate(-50%,-50%)" }} />
                </div>
                <div className="w-[74%] max-w-full rounded-2xl bg-white/92 p-[2%] shadow-lg">{resultSlot}</div>
                <button type="button" onClick={replay} className="crt-btn" style={{ position: "static", width: "26%" }}>
                  <img src={`${ASSET}/complete/button_choi_lai.png`} alt="Chơi lại" />
                </button>
              </div>
            ) : (
              <>
                <div className="absolute" style={{ left: "18%", top: "3%", width: "67%", height: "79%" }}>
                  <img src={`${ASSET}/complete/khung_hoan_thanh.png`} alt="Hoàn thành!" className="absolute inset-0 h-full w-full object-contain" aria-hidden />
                  <img src={scoreImg} alt={`${harvested}`} className="crt-scorepop absolute left-1/2 top-[57%] w-[16%] h-[34%] object-contain" style={{ transform: "translate(-50%,-50%)" }} />
                </div>
                <img src={`${ASSET}/complete/tho_trang_cam_ca_rot.png`} alt="" className="crt-rabbit-happy absolute bottom-[1%] left-[1.5%] w-[18%] h-[47%] object-contain" style={{ objectPosition: "center bottom" }} aria-hidden />
                <button type="button" onClick={replay} className="crt-btn crt-btn-cx" style={{ left: "50%", bottom: "2.5%", width: "26%", height: "14%" }}>
                  <img src={`${ASSET}/complete/button_choi_lai.png`} alt="Chơi lại" />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
